import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { ResponseUserType } from "@/types/service";
import { useExternalUserList } from "@/hooks";
import { LodashUtils } from "@/utils";
import { LangConstant } from "@/const";

type SelectedUsersProps = {
  users: ResponseUserType[];
  addUser: (user: ResponseUserType) => void;
  removeUser: (user: ResponseUserType) => void;
};

const SelectedUsers: FC<SelectedUsersProps> = ({ users, addUser, removeUser }) => {
  const { t: getLabel } = useTranslation();
  const { users: totalUsers, isLoading } = useExternalUserList({ page: 1, size: 1000 });

  const handleChange = (_: unknown, value: ResponseUserType | null) => {
    if (value) addUser(value);
  };

  return (
    <Box>
      <Autocomplete
        disablePortal
        loading={isLoading}
        loadingText={getLabel("L_LOADING")}
        sx={{ width: "100%", marginBottom: "16px" }}
        onChange={handleChange}
        options={totalUsers}
        getOptionKey={user => user.uid}
        getOptionLabel={user => user.name}
        getOptionDisabled={user => LodashUtils.includes(users, user)}
        renderInput={params => (
          <TextField
            {...params}
            sx={{ marginTop: "8px" }}
            label={getLabel("L_SEARCH")}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
      <Typography variant="subtitle2" marginBottom="8px">
        {getLabel("L_SELECT_USER_TITLE", { ns: LangConstant.NS_REQUEST })}:
      </Typography>
      <TableContainer component={Paper} sx={{ marginBottom: "8px" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">{getLabel("L_USER")}</TableCell>
              <TableCell align="center">{getLabel("L_EMAIL")}</TableCell>
              <TableCell align="center">
                {getLabel("L_SYSTEM_IN_CHARGE", {
                  ns: LangConstant.NS_REQUEST,
                })}
              </TableCell>
              <TableCell align="center"> </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length > 0 ? (
              users.map(user => (
                <TableRow key={user.name} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell align="center">{user.name}</TableCell>
                  <TableCell align="center">{user.email}</TableCell>
                  <TableCell align="center">{user.unit}</TableCell>
                  <TableCell align="center" onClick={() => removeUser(user)}>
                    <IconButton size="small">
                      <Delete fontSize="small" color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow sx={{ border: 0 }}>
                <TableCell colSpan={4} align="center">
                  {getLabel("L_NO_DATA")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SelectedUsers;
